import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api-client.js";

const REPOS_KEY = ["repos"] as const;

export function useRepos() {
  return useQuery({
    queryKey: REPOS_KEY,
    queryFn: () => api.listRepos(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useRepo(slug: string) {
  return useQuery({
    queryKey: ["repo", slug] as const,
    queryFn: () => api.getRepo(slug),
    refetchInterval: 15_000,
    staleTime: 5_000,
    enabled: slug.length > 0,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["repos"] });
  void qc.invalidateQueries({ queryKey: ["repo"] });
}

export function usePauseCronJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cronJobId: string) => api.pauseCronJob(cronJobId),
    onSuccess: () => invalidateAll(qc),
  });
}
export function useResumeCronJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cronJobId: string) => api.resumeCronJob(cronJobId),
    onSuccess: () => invalidateAll(qc),
  });
}
export function useRunCronJobNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cronJobId: string) => api.runCronJobNow(cronJobId),
    onSuccess: () => {
      invalidateAll(qc);
      // The agent polls every 5s and acks immediately. Fire a few extra
      // refetches so the badge flips pending→started without waiting the
      // full 10s list interval.
      [1500, 4000, 7000].forEach((ms) =>
        setTimeout(() => invalidateAll(qc), ms),
      );
    },
  });
}
export function useSetCronJobSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cronJobId, schedule }: { cronJobId: string; schedule: string }) =>
      api.setCronJobSchedule(cronJobId, schedule),
    onSuccess: () => invalidateAll(qc),
  });
}
export function useSetCronJobWrapped() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cronJobId, wrapped }: { cronJobId: string; wrapped: boolean }) =>
      api.setCronJobWrapped(cronJobId, wrapped),
    onSuccess: () => invalidateAll(qc),
  });
}
